import scrubAdf from '@atlaskit/adf-utils/scrub-adf';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { isGoogleCloudPlatform } from '@atlaskit/atlassian-context/cloud-provider';
import type {
	BatchAttrsStepPM,
	InlineCommentAddNodeMarkStepPM,
	InlineCommentStepPM,
	NodeJson,
	OverrideDocumentStepPM,
	ReplaceAroundStepPM,
	ReplaceStepPM,
	SetAttrsStepPM,
	StepJson,
	StepMetadata,
} from '@atlaskit/editor-common/collab';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform-override';
import { sendableSteps } from '@atlaskit/prosemirror-collab';

import { CustomError } from '../errors/custom-errors';
import type { ProductInformation } from '../types';

const GCP_STAGING_TENANT_PATTERN = /^.*-cdp-\w+\.jira-dev\.com$/u;

export const createLogger =
	(prefix: string, color: string = 'blue') =>
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(msg: string, data: any = null): void => {
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).COLLAB_PROVIDER_LOGGER) {
			// eslint-disable-next-line no-console
			console.log(`%cCollab-${prefix}: ${msg}`, `color: ${color}; font-weight: bold`, data);
		}
	};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sleep(ms: number): Promise<any> {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

/**
 * Returns whether an id belongs to an agent — referred to as an "AI provider" in this
 * package's terminology.
 *
 * @remarks Agents are not a distinct type: they are ordinary participants distinguished
 * only by the `agent:` prefix on their `userId`/`sessionId`. This predicate is the single
 * source of truth for that convention and gates agent-specific presence handling across the
 * provider (e.g. immediate `presence` emission and agent-leave cleanup).
 */
export const isAIProviderID = (id: string): boolean =>
	typeof id === 'string' && id.startsWith('agent:');

const IDENTITY_USER_ARI_PREFIX = 'ari:cloud:identity::user/';

/**
 * Normalises an agent identifier that may arrive as an identity ARI
 * (`ari:cloud:identity::user/<aaid>`) down to the bare AAID. Any other value is
 * returned unchanged.
 */
export const normalizeAgentId = (agentId: string): string =>
	agentId.startsWith(IDENTITY_USER_ARI_PREFIX)
		? agentId.slice(IDENTITY_USER_ARI_PREFIX.length)
		: agentId;

/**
 * Derives the synthetic AI-provider participant id (`agent:<id>`) for an
 * agent-authored step, or `undefined` if the step is not agent-authored.
 *
 * `agentType` is the reliable "made by an agent" signal (present even when
 * `agentId` is not). We key on the agent's AAID when available, otherwise the
 * agent type (e.g. `mcp`/`twg`), otherwise a generic `agent`.
 */
export const getAgentProviderId = (step: {
	agentId?: string;
	agentType?: string;
}): string | undefined => {
	if (!step.agentType) {
		return undefined;
	}
	const id = step.agentId ? normalizeAgentId(step.agentId) : step.agentType;
	return `agent:${id || 'agent'}`;
};

export const getProduct = (productInfo?: ProductInformation): string =>
	productInfo?.product ?? 'unknown';

export const getSubProduct = (productInfo?: ProductInformation): string =>
	productInfo?.subProduct ?? (!!productInfo?.product ? 'none' : 'unknown');

interface Step {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
	from?: number;
	gapFrom?: number;
	gapTo?: number;
	insert?: number;
	stepType: string;
	to?: number;
	userId?: string;
}

export type UGCFreeStepDetails = {
	contentTypes: string;
	stepSizeInBytes?: number;
	type: string;
};

// Get as step info which is known not to contain user generated content.
export const getStepUGCFreeDetails = (step: ProseMirrorStep): UGCFreeStepDetails => {
	let stepJson: Step | null;
	try {
		stepJson = step.toJSON() as Step;
	} catch (e) {
		return {
			type: 'unknown',
			contentTypes: '',
			stepSizeInBytes: Buffer.byteLength(JSON.stringify(step)),
		};
	}

	let contentTypes: string = '';
	if (stepJson.slice?.content && Array.isArray(stepJson.slice?.content)) {
		contentTypes = stepJson.slice.content
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			.map((c: any) => {
				return c?.type || 'unknown';
			})
			.join(', ');
	}
	return {
		type: stepJson.stepType || 'unknown',
		contentTypes,
		stepSizeInBytes: Buffer.byteLength(JSON.stringify(step)),
	};
};

const stepWithNextDocument = (stepJson: StepJson): stepJson is OverrideDocumentStepPM => {
	return stepJson.stepType === 'override-document' && 'nextDocument' in stepJson;
};

const stepWithMark = (stepJson: StepJson): stepJson is InlineCommentStepPM => {
	return stepJson.stepType === 'addMark' || stepJson.stepType === 'addNodeMark';
};

const stepWithAttrs = (stepJson: StepJson): stepJson is SetAttrsStepPM => {
	return stepJson.stepType === 'setAttrs' && 'attrs' in stepJson;
};

const stepWithBatchAttrs = (
	stepJson: StepJson | BatchAttrsStepPM,
): stepJson is BatchAttrsStepPM => {
	return stepJson.stepType === 'batchAttrs' && 'data' in stepJson;
};

const stepWithFromTo = (stepJson: StepJson): stepJson is ReplaceStepPM | ReplaceAroundStepPM => {
	return (
		'from' in stepJson &&
		typeof stepJson.from === 'number' &&
		'to' in stepJson &&
		typeof stepJson.to === 'number'
	);
};

const stepWithGapFromTo = (stepJson: StepJson): stepJson is ReplaceAroundStepPM => {
	return (
		'gapFrom' in stepJson &&
		typeof stepJson.gapFrom === 'number' &&
		'gapTo' in stepJson &&
		typeof stepJson.gapTo === 'number'
	);
};

const stepWithInsert = (stepJson: StepJson): stepJson is ReplaceAroundStepPM => {
	return 'insert' in stepJson && typeof stepJson.insert === 'number';
};

const stepWithPos = (
	stepJson: StepJson,
): stepJson is SetAttrsStepPM | InlineCommentAddNodeMarkStepPM => {
	return 'pos' in stepJson && typeof stepJson.pos === 'number';
};

const stepWithSlice = (stepJson: StepJson): stepJson is ReplaceAroundStepPM | ReplaceStepPM => {
	return 'slice' in stepJson && Array.isArray(stepJson.slice?.content);
};

// Get as step info which is known not to contain user generated content.
export const getStepTypes = (
	stepJson: StepJson,
): {
	contentTypes: string | null;
	type: string;
} => {
	let contentTypes: string | null = null;

	if (stepWithSlice(stepJson)) {
		contentTypes = stepJson.slice.content
			.map((c) => {
				return c?.type || 'unknown';
			})
			.join(', ');
	}
	return {
		type: stepJson.stepType || 'unknown',
		contentTypes,
	};
};

export const getStepsAdfWithObfuscation = (stepJson: StepJson): ADFEntity[] | null => {
	const stepContentAsAdf: ADFEntity[] | null = stepToAdf(stepJson);
	if (!stepContentAsAdf) {
		return null;
	}

	const scrubbedSteps = stepContentAsAdf
		.map((adf) => scrubAdf(adf))
		.filter((adf): adf is ADFEntity => !!adf);

	return scrubbedSteps;
};

export const getDocAdfWithObfuscation = (doc: ProseMirrorNode): ADFEntity | null => {
	const docJson = doc.toJSON() as JSONDocNode;

	const scrubbedDoc = scrubAdf(docJson);
	if (!scrubbedDoc) {
		return null;
	}

	return scrubbedDoc;
};

export const getDocAdfWithObfuscationFromJSON = (docJson: JSONDocNode): ADFEntity | null => {
	const scrubbedDoc = scrubAdf(docJson);
	if (!scrubbedDoc) {
		return null;
	}

	return scrubbedDoc;
};

export const getStepPositions = (
	stepJson: StepJson,
): {
	from?: number | undefined;
	gapFrom?: number | undefined;
	gapTo?: number | undefined;
	insert?: number | undefined;
	pos?: number | undefined;
	to?: number | undefined;
} => {
	return {
		...(stepWithFromTo(stepJson) && { from: stepJson.from, to: stepJson.to }),
		...(stepWithGapFromTo(stepJson) && { gapFrom: stepJson.gapFrom, gapTo: stepJson.gapTo }),
		...(stepWithInsert(stepJson) && { insert: stepJson.insert }),
		...(stepWithPos(stepJson) && { pos: stepJson.pos }),
	};
};

/**
 * Returns the metadata for Step
 * @description metadata is applied by transform overrides [here](https://bitbucket.org/atlassian/adf-schema/src/e13bbece84ede8f245067dc53dd7ce694f427eda/packages/editor-prosemirror/transform-override.ts#lines-12)
 */
const getStepMetadata = (stepJson: StepJson): StepMetadata['metadata'] | undefined => {
	return stepJson.metadata;
};

export const getObfuscatedSteps = (
	steps: StepJson[],
	endIndex: number | undefined = undefined,
): {
	stepContent: ADFEntity[] | null;
	stepMetadata:
		| {
				createdOffline?: boolean;
				prevStepId?: string;
				rebased?: boolean;
				reqId?: string;
				schemaVersion?: string;
				source?: string;
				stepId?: string;
				traceId?: string;
				unconfirmedStepAfterRecovery?: boolean;
		  }
		| undefined;
	stepPositions: {
		from?: number | undefined;
		gapFrom?: number | undefined;
		gapTo?: number | undefined;
		insert?: number | undefined;
		pos?: number | undefined;
		to?: number | undefined;
	};
	stepType: {
		contentTypes: string | null;
		type: string;
	};
}[] => {
	return steps.slice(0, endIndex).map((step) => {
		return {
			stepType: getStepTypes(step),
			stepContent: getStepsAdfWithObfuscation(step),
			stepPositions: getStepPositions(step),
			stepMetadata: getStepMetadata(step),
		};
	});
};

const stepToAdf = (step: StepJson): ADFEntity[] | null => {
	if (stepWithSlice(step)) {
		return [
			{
				type: 'doc',
				content: step.slice.content.filter((el): el is NodeJson => el !== null),
			},
		];
	} else if (stepWithNextDocument(step)) {
		return [
			{
				type: 'doc',
				content: step.nextDocument.content,
			},
		];
	} else if (stepWithMark(step) && step.mark) {
		return [
			{
				type: 'doc',
				marks: [
					{
						type: step.mark.type || 'unknown',
						attrs: step.mark.attrs,
					},
				],
			},
		];
	} else if (stepWithAttrs(step)) {
		return [
			{
				type: 'doc',
				attrs: step.attrs,
			},
		];
	} else if (stepWithBatchAttrs(step)) {
		return step.data.map((stepData) => {
			return {
				type: 'doc',
				attrs: stepData.attrs,
			};
		});
	}
	return [];
};

export async function logObfuscatedSteps(
	oldState: EditorState | null,
	newState: EditorState,
): Promise<
	| CustomError
	| {
			stepsFromNewState: string;
			stepsFromOldState: string;
	  }
> {
	try {
		let stepsFromOldState = '',
			stepsFromNewState = '';

		const states = {
			old: oldState ? sendableSteps(oldState) : null,
			new: sendableSteps(newState),
		};

		if (states.new?.steps) {
			stepsFromNewState = await toObfuscatedSteps(states.new.steps);
		}

		if (states.old?.steps) {
			stepsFromOldState = await toObfuscatedSteps(states.old.steps);
		}

		return {
			stepsFromOldState,
			stepsFromNewState,
		};
	} catch (err) {
		return new CustomError('Failed to obfuscate steps', err);
	}
}

export async function toObfuscatedSteps(steps: readonly ProseMirrorStep[]): Promise<string> {
	const _steps = await Promise.resolve(steps.slice().map<StepJson>((s) => s.toJSON()));
	return JSON.stringify(getObfuscatedSteps(_steps));
}

/**
 * Keep all three checks: AWS-served Bifrost responses can overwrite the shared
 * detector's cookie to AWS even for a GCP tenant (https://hello.atlassian.net/browse/EDGE-10729).
 * The Edge cookie covers that case; the shared detector also supports SSR, and
 * the hostname check covers missing or unreliable cloud-provider cookies in staging.
 * Any GCP signal is sufficient; an AWS value must not veto the remaining checks.
 */
export function isGCPtenant(hostname?: string): boolean {
	let edgeProvider: string | undefined;
	try {
		edgeProvider =
			typeof document === 'undefined'
				? undefined
				: document.cookie
						.split(';')
						.map((cookie) => cookie.trim())
						.find((cookie) => cookie.startsWith('Atl-Ctx-Cloud-Service-Provider='))
						?.split('=')[1];
	} catch {
		// Cookie access can be unavailable; continue with the shared detector and staging fallback.
	}

	return (
		edgeProvider === 'GCP' ||
		isGoogleCloudPlatform() ||
		Boolean(hostname && GCP_STAGING_TENANT_PATTERN.test(hostname))
	);
}
